// Stroke analysis shared by server (tutor timeline) and web (process view, replay). Pure: no DOM.
import type { EraseEvent, HoverEvent, InkEvent, SayEvent, StrokeEvent, StuckMark } from './practice.ts';

export const BURST_GAP_MS = 1500;   // strokes closer than this belong to one burst
export const PAUSE_MS = 8000;       // a gap this long is reported as a pause

export interface Box { x0: number; y0: number; x1: number; y1: number }
export interface Page { w: number; h: number }
export interface AnalysedStroke extends StrokeEvent { index: number; burst: number }
export interface Burst { i: number; strokes: AnalysedStroke[]; t0: number; t1: number; box: Box }
export interface Analysis {
  page: Page;
  strokes: AnalysedStroke[];
  erasedAt: Map<string, number>;
  bursts: Burst[];
  says: SayEvent[];
  hovers: HoverEvent[];
  erases: EraseEvent[];
  end: number;
}

export const tStart = (e: InkEvent): number => (e.k === 'stroke' || e.k === 'hover') ? e.t0 : e.t;
const tEnd = (e: InkEvent): number => ('t1' in e && e.t1 != null) ? e.t1 : tStart(e);

/** Drop retried duplicates (same n) and order by start time. */
export function normalize(events: readonly InkEvent[]): InkEvent[] {
  const seen = new Set<number>(), out: InkEvent[] = [];
  for (const e of events) { if (seen.has(e.n)) continue; seen.add(e.n); out.push(e); }
  return out.sort((a, b) => tStart(a) - tStart(b) || a.n - b.n);
}

export function bbox(strokes: readonly { pts: readonly (readonly number[])[] }[]): Box {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const s of strokes) for (const p of s.pts) {
    const x = p[0]!, y = p[1]!;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  return { x0, y0, x1, y1 };
}

export function region(x: number, y: number, page: Page): string {
  const col = x < page.w / 3 ? 'left' : x < 2 * page.w / 3 ? 'centre' : 'right';
  const row = y < page.h / 3 ? 'top' : y < 2 * page.h / 3 ? 'middle' : 'bottom';
  return `${row}-${col}`;
}

/** The latest page event defines the page (the paper can grow). */
export function analyse(events: readonly InkEvent[]): Analysis {
  let page: Page = { w: 700, h: 560 };
  for (const e of events) if (e.k === 'page') page = { w: e.w, h: e.h };
  const strokes = events.filter((e): e is StrokeEvent => e.k === 'stroke').sort((a, b) => a.t0 - b.t0)
    .map((s, index) => ({ ...s, index, burst: 0 }));
  const erasedAt = new Map<string, number>();
  for (const e of events) if (e.k === 'erase') for (const id of e.ids) if (!erasedAt.has(id)) erasedAt.set(id, e.t);
  const bursts: Burst[] = [];
  let b: Burst | null = null;
  for (const s of strokes) {
    if (!b || s.t0 - b.t1 >= BURST_GAP_MS) { b = { i: bursts.length + 1, strokes: [], t0: s.t0, t1: s.t1, box: { x0: 0, y0: 0, x1: 0, y1: 0 } }; bursts.push(b); }
    b.strokes.push(s); b.t1 = Math.max(b.t1, s.t1); s.burst = b.i;
  }
  for (const x of bursts) x.box = bbox(x.strokes);
  const end = events.reduce((m, e) => Math.max(m, tEnd(e)), 0);
  return {
    page, strokes, erasedAt, bursts, end,
    says: events.filter((e): e is SayEvent => e.k === 'say'),
    hovers: events.filter((e): e is HoverEvent => e.k === 'hover'),
    erases: events.filter((e): e is EraseEvent => e.k === 'erase'),
  };
}

export function fmt(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function hoverIn(A: Analysis, a: number, b: number) {
  let covered = 0; const pts: [number, number, number][] = [];
  for (const h of A.hovers) {
    const lo = Math.max(a, h.t0), hi = Math.min(b, h.t1);
    if (hi > lo) { covered += hi - lo; for (const p of h.pts) if (h.t0 + p[2] >= a && h.t0 + p[2] <= b) pts.push(p); }
  }
  return { frac: covered / Math.max(1, b - a), pts };
}

/** Compact text timeline for the tutor. Stuck marks are the learner's own ground truth: excluded unless passed. */
export function timeline(prompt: string, events: readonly InkEvent[], marks: readonly StuckMark[] = []): string {
  const A = analyse(events);
  const items: { t: number; text: string }[] = [];
  const pause = (a: number, b: number) => {
    if (b - a < PAUSE_MS) return;
    const h = hoverIn(A, a, b);
    const mid = h.pts[h.pts.length >> 1];
    const where = mid ? ` over ${region(mid[0], mid[1], A.page)}` : '';
    items.push({ t: a, text: `${fmt(a)}  PAUSE ${Math.round((b - a) / 1000)}s, ${h.frac >= 0.3 ? `pen hovering ${Math.round(h.frac * 100)}%${where}` : 'pen away from the glass'}` });
  };
  let prevEnd = 0;
  for (const b of A.bursts) {
    pause(prevEnd, b.t0);
    const cx = (b.box.x0 + b.box.x1) / 2, cy = (b.box.y0 + b.box.y1) / 2;
    const gone = b.strokes.filter(s => A.erasedAt.has(s.id)).length;
    items.push({ t: b.t0, text: `${fmt(b.t0)}–${fmt(b.t1)}  B${b.i}  ${b.strokes.length} stroke${b.strokes.length > 1 ? 's' : ''}, ${region(cx, cy, A.page)}, ${Math.round(b.box.x1 - b.box.x0)}×${Math.round(b.box.y1 - b.box.y0)} px${gone ? `  (${gone} erased later)` : ''}` });
    prevEnd = b.t1;
  }
  if (A.end - prevEnd >= PAUSE_MS) pause(prevEnd, A.end);
  const burstOf = new Map(A.strokes.map(s => [s.id, s.burst]));
  for (const e of A.erases) {
    const hit = [...new Set(e.ids)].filter(id => burstOf.has(id));
    if (!hit.length) continue;
    const from = [...new Set(hit.map(id => burstOf.get(id)!))];
    items.push({ t: e.t, text: `${fmt(e.t)}  ERASE ${hit.length} stroke${hit.length > 1 ? 's' : ''} from ${from.map(i => 'B' + i).join(', ')} (${e.via})` });
  }
  for (const s of A.says) items.push({ t: s.t, text: `${fmt(s.t)}  SAY "${s.text}"` });
  for (const m of marks) items.push({ t: m.t, text: `${fmt(m.t)}  MARK stuck (learner's replay mark)${m.note ? ': ' + m.note : ''}` });
  items.sort((a, b) => a.t - b.t);
  const erased = A.erasedAt.size;
  const head = `PAGE "${prompt}" · ${fmt(A.end)} · ${A.strokes.length} strokes${erased ? ` (${erased} erased)` : ''} · ${A.bursts.length} bursts · page ${A.page.w}×${A.page.h} px`;
  return [head, ...items.map(i => i.text)].join('\n');
}

/** Bursts containing strokes drawn after `sinceT` (e.g. since the tutor's last reply), as page-px boxes. */
export function inkSince(events: readonly InkEvent[], sinceT: number): { burst: string; box: [number, number, number, number] }[] {
  const A = analyse(events);
  const fresh = new Set(A.strokes.filter(s => s.t0 > sinceT && !A.erasedAt.has(s.id)).map(s => s.id));
  return A.bursts.filter(b => b.strokes.some(s => fresh.has(s.id))).map(b => {
    const box = bbox(b.strokes.filter(s => fresh.has(s.id)));
    return { burst: 'B' + b.i, box: [Math.round(box.x0), Math.round(box.y0), Math.round(box.x1 - box.x0), Math.round(box.y1 - box.y0)] };
  });
}
